import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderActions,
	CardHeading,
	I18nFormProvider,
	I18nLocaleSwitcher,
	LinkTabs,
	NotFound,
	useDynamicConfig,
} from "@dgs/core";
import { Form, Formik } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes } from "react-router";
import styled from "styled-components";
import { EventFormValues, EventSaveRequest } from "../../types/event";
import { eventSteps, useFilteredEventSteps } from "../eventSteps";
import { EventAddressClearButton } from "./EventAddressClearButton";
import { EventAddressStepFormFields } from "./EventAddressStepFormFields";
import { EventBaseDataStepFormFields } from "./EventBaseDataStepFormFields";
import { EventDetailsStepFormFields } from "./EventDetailsStepFormFields";
import { mapEventFormValuesToRequest } from "./eventFormUtils";
import { EventParticipationStepFormFields } from "./EventParticipationStepFormFields";
import { EventPlannedCostStepFormFields } from "./EventPlannedCostStepFormFields";
import { emptyTranslation } from "./eventStepperUtils";
import { EventUsersStepFormFields } from "./EventUsersStepFormFields";
import { validateEventForm } from "./eventValidation";
import { LinkTabWithError } from "./LinkTabWithError";
import { UpdateStepperFooter } from "./UpdateStepperFooter";

const StyledForm = styled(Form)`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacing(6)};
`;

interface Props {
	variant?: "elevated" | "outlined";
	initialValues: EventFormValues;
	onSubmit: (saveRequest: EventSaveRequest) => Promise<void>;
}

export const UpdateEvent: FC<Props> = ({ initialValues, onSubmit, variant }) => {
	const { t } = useTranslation();
	const { languageSettings } = useDynamicConfig();
	const { filteredSteps, currentStep } = useFilteredEventSteps(initialValues.eventType);

	return (
		<I18nFormProvider initialLanguage={languageSettings.fallback}>
			<Formik
				initialValues={initialValues}
				onSubmit={async (values) => {
					return onSubmit(mapEventFormValuesToRequest(values));
				}}
				validate={validateEventForm(t)}
			>
				{(formik) => (
					<StyledForm>
						<LinkTabs>
							{filteredSteps.map((step, index) => {
								const hasError = !step.isValid(formik.errors);
								const label = t(eventSteps.find((s) => s.key === step.key)?.label as string);
								return (
									<LinkTabWithError key={index} to={`../${step.key}`} hasError={hasError}>
										{label}
									</LinkTabWithError>
								);
							})}
						</LinkTabs>
						<Card $variant={variant}>
							<CardHeader>
								<CardHeading>{t(eventSteps.find((s) => s.key === currentStep.key)?.label as string)}</CardHeading>
								<CardHeaderActions>
									{currentStep.key === "base-data" && <I18nLocaleSwitcher emptyTranslation={emptyTranslation} />}
									{currentStep.key === "address" && <EventAddressClearButton />}
								</CardHeaderActions>
							</CardHeader>
							<CardBody>
								<Routes>
									<Route index element={<Navigate to="../base-data" />} />
									<Route path="base-data" element={<EventBaseDataStepFormFields eventTypeDisabled={true} />} />
									<Route path="address" element={<EventAddressStepFormFields />} />
									<Route path="users" element={<EventUsersStepFormFields />} />
									<Route path="participation" element={<EventParticipationStepFormFields />} />
									<Route path="planned-cost" element={<EventPlannedCostStepFormFields />} />
									<Route path="details" element={<EventDetailsStepFormFields />} />
									<Route path="*" element={<NotFound />} />
								</Routes>
							</CardBody>
							<UpdateStepperFooter currentStepKey={currentStep.key} filteredSteps={filteredSteps} />
						</Card>
					</StyledForm>
				)}
			</Formik>
		</I18nFormProvider>
	);
};
